// hooks/usePlanChangeHandlers.ts
/**
 * Hook to wrap plan operations with change notifications and additional handlers.
 * Extracts plan change handling logic from App.js.
 */

import { useCallback } from 'react';
import { typedApi } from '../services/typedApiClient';
import { toBackendSemesterCode } from '../utils/semesterUtils';
import { extractSemestersToDelete } from '../utils/destructiveSemester';
import type { AcademicPlan, EnrolledCourse } from '../types';
import type { PlanOperations } from './useAcademicPlan';
import type { CourseInput } from './academicPlan/useCourseOperations';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Plan change event detail
 */
export interface PlanChangeEvent {
  type: 'course_added' | 'course_removed' | 'course_moved' | 'all_courses_cleared' | 'semester_cleared' | 'template_applied' | 'semester_deleted';
  course?: CourseInput;
  semester?: string;
  fromSemester?: string;
  toSemester?: string;
  distributionPlan?: unknown;
}

/**
 * The canonical plan operations type produced by useAcademicPlan.
 * Re-exported here so callers wanting the "original" (un-wrapped) ops
 * have a stable, backwards-compatible name.
 */
export type OriginalPlanOps = PlanOperations;

/**
 * Toast handler types
 */
export type ShowSuccessHandler = (message: string, options?: { duration?: number }) => void;
export type ShowErrorHandler = (message: string, options?: { duration?: number }) => void;

/**
 * Parameters for the usePlanChangeHandlers hook
 */
export interface UsePlanChangeHandlersParams {
  originalPlanOps: OriginalPlanOps;
  academicPlan: AcademicPlan | null;
  currentPlanId: number | null;
  user: { id?: string } | null;
  showSuccess: ShowSuccessHandler;
  showError: ShowErrorHandler;
}

/**
 * Enhanced plan operations with change notifications
 */
export interface EnhancedPlanOps extends OriginalPlanOps {
  deleteSemester: (semesterToDelete: string) => Promise<void>;
}

/**
 * Return type for the usePlanChangeHandlers hook
 */
export interface UsePlanChangeHandlersReturn {
  planOps: EnhancedPlanOps;
  handlePlanChange: (event?: PlanChangeEvent) => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to wrap plan operations with change notifications and additional handlers.
 */
export function usePlanChangeHandlers({
  originalPlanOps,
  academicPlan,
  currentPlanId,
  user,
  showSuccess,
  showError
}: UsePlanChangeHandlersParams): UsePlanChangeHandlersReturn {
  // Create a plan change handler for triggering refreshes/updates
  // The HierarchicalDegreeAudit component handles its own data refresh
  // This handler is available for future use if needed
  const handlePlanChange = useCallback((_event?: PlanChangeEvent) => {
    // No-op for now - components handle their own refreshes
  }, []);

  // Delete semester handler - reuse existing endpoint
  const handleDeleteSemester = useCallback(async (semesterToDelete: string): Promise<void> => {
    try {
      if (!currentPlanId) {
        throw new Error('No active plan found');
      }

      // Get current semester list and remove the target semester
      const currentSemesters = Object.keys(academicPlan?.semesters || {});
      const updatedSemesters = currentSemesters.filter(semester => semester !== semesterToDelete);

      // Optimistically update local state first for immediate UI feedback
      if (originalPlanOps.updateAcademicPlan) {
        originalPlanOps.updateAcademicPlan(prev => {
          const newSemesters = { ...prev.semesters };
          delete newSemesters[semesterToDelete];

          return {
            ...prev,
            semesters: newSemesters
          };
        });
      }

      // Reuse existing endpoint - same as SemesterManagementModal.
      // The user already confirmed deleting THIS semester (+ its courses) in
      // SemesterHeaderMenu, so deleting only the target is pre-authorized.
      try {
        await typedApi.academicPlans.updateSemesters(currentPlanId, updatedSemesters);
      } catch (err) {
        const toDelete = extractSemestersToDelete(err);
        if (toDelete === null) throw err; // not our guard — fall to outer catch

        // RC5-1 anomaly detection: the backend reports every populated semester
        // this request would delete. If that set is anything beyond the one the
        // user chose, our keep-list was built from stale state — refuse and
        // resync rather than silently wiping the extras.
        const targetCode = toBackendSemesterCode(semesterToDelete);
        const extras = toDelete.filter(s => s.semester_code !== targetCode);
        if (extras.length > 0) {
          if (originalPlanOps.refreshPlan) await originalPlanOps.refreshPlan();
          const names = extras.map(s => `${s.semester_name} (${s.course_count})`).join(', ');
          showError(
            `Your plan was out of sync, so this delete would also have removed ` +
            `courses from: ${names}. Nothing was deleted — the plan has been ` +
            `refreshed, please try again.`
          );
          return;
        }

        // Only the intended semester would be deleted → already confirmed; retry.
        await typedApi.academicPlans.updateSemesters(currentPlanId, updatedSemesters, true);
      }

      // Show success message
      showSuccess('Semester deleted successfully', { duration: 3000 });

      // Trigger plan change notification
      handlePlanChange({ type: 'semester_deleted', semester: semesterToDelete });

    } catch (err) {
      const error = err as Error;
      // Revert optimistic update on error by refreshing the plan
      if (originalPlanOps.refreshPlan) {
        await originalPlanOps.refreshPlan();
      }

      showError(error.message || 'Failed to delete semester. Please try again.');
    }
  }, [academicPlan, originalPlanOps, currentPlanId, showSuccess, showError, handlePlanChange]);

  // Wrap planOps to add plan change notifications and delete semester functionality
  const planOps: EnhancedPlanOps = {
    ...originalPlanOps,
    addCourse: async (course, semester) => {
      const added = await originalPlanOps.addCourse(course, semester);
      if (!course?.isPlaceholder) {
        handlePlanChange({ type: 'course_added', course, semester });
      }
      return added;
    },
    removeCourse: async (semester, courseIndex, skipConfirmation) => {
      await originalPlanOps.removeCourse(semester, courseIndex, skipConfirmation);
      handlePlanChange({ type: 'course_removed', semester });
    },
    moveCourse: async (course, fromSemester, toSemester, fromIndex) => {
      await originalPlanOps.moveCourse(course, fromSemester, toSemester, fromIndex);
      if (!course?.isPlaceholder) {
        handlePlanChange({ type: 'course_moved', course, fromSemester, toSemester });
      }
    },
    // MUST forward options: the #205 dialog passes {includePlaceholders,
    // skipConfirm}, and a zero-arg decorator here silently swallowed them —
    // () => void is assignable to (options?) => void, so tsc cannot catch
    // this. It cost a full staging-rehearsal debugging arc.
    clearAllCourses: async (options) => {
      await originalPlanOps.clearAllCourses(options);
      handlePlanChange({ type: 'all_courses_cleared' });
    },
    clearSemester: async (semester) => {
      await originalPlanOps.clearSemester(semester);
      handlePlanChange({ type: 'semester_cleared', semester });
    },
    applyTemplate: async (distributionPlan) => {
      await originalPlanOps.applyTemplate(distributionPlan as Record<string, EnrolledCourse[]>);
      handlePlanChange({ type: 'template_applied', distributionPlan });
    },
    deleteSemester: handleDeleteSemester,
  };

  return {
    planOps,
    handlePlanChange
  };
}
